import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Rocket, Users, Target, Zap, FileText, Inbox, Brain, 
  BarChart3, ArrowRight, CheckCircle, Building2, Lightbulb,
  X, Star, Clock, MapPin, Calendar, Briefcase, GraduationCap, Landmark, Loader2
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { programsAPI, getImageUrl } from '../services/api';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import axios from 'axios';

const Home = () => {
  const { language } = useLanguage();
  const t = translations[language].home;
  const [isAnnual, setIsAnnual] = useState(false);
  const [featuredPrograms, setFeaturedPrograms] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [quickReg, setQuickReg] = useState({ email: '', organization: '', name: '' });
  const [quickRegLoading, setQuickRegLoading] = useState(false);
  const [quickRegDone, setQuickRegDone] = useState(false);
  const [quickRegError, setQuickRegError] = useState('');
  useEffect(() => {
    const loadFeatured = async () => {
      try {
        let data = await programsAPI.getFeatured();
        if (!data || data.length === 0) {
          data = await programsAPI.getAll();
        }
        setFeaturedPrograms(data.slice(0, 3));
      } catch (e) {
        // silently fail - this section is optional
      }
    };
    loadFeatured();
  }, []);

  const howItWorks = [
    {
      step: '1',
      title: t.howItWorks.step1.title,
      description: t.howItWorks.step1.description,
    },
    {
      step: '2',
      title: t.howItWorks.step2.title,
      description: t.howItWorks.step2.description,
    },
    {
      step: '3',
      title: t.howItWorks.step3.title,
      description: t.howItWorks.step3.description,
    },
    {
      step: '4',
      title: t.howItWorks.step4.title,
      description: t.howItWorks.step4.description,
    },
  ];

  const features = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: t.features.feature1.title,
      description: t.features.feature1.description,
    },
    {
      icon: <Inbox className="w-8 h-8" />,
      title: t.features.feature2.title,
      description: t.features.feature2.description,
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: t.features.feature3.title,
      description: t.features.feature3.description,
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: t.features.feature4.title,
      description: t.features.feature4.description,
    },
  ];

  const stats = [
    { value: '20+', label: t.stats.stat1 },
    { value: '6.000+', label: t.stats.stat2 },
    { value: '20+', label: t.stats.stat3 },
    { value: '1.000+', label: t.stats.stat4 },
  ];

  return (
    <div className="min-h-screen">
      {/* 1. Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden" style={{ backgroundColor: '#6949b6' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_innovate-nexus-1/artifacts/hxel7nka_BRAND2020_1.png" 
              alt="ColaboraChile"
              className="h-32 md:h-40 lg:h-48 w-auto mx-auto mb-8"
            />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {language === 'es' ? (
                <>
                  <span className="block">No evalúes solo.</span>
                  <span className="block"><span className="text-blue-400">Evalúa con Humanos + IA</span></span>
                </>
              ) : (
                <>
                  <span className="block">Don't Evaluate Alone.</span>
                  <span className="block"><span className="text-blue-400">Evaluate with Humans + AI</span></span>
                </>
              )}
            </h1>
            <p className="text-base sm:text-lg text-purple-200 font-medium mb-8 max-w-3xl mx-auto">
              {language === 'es'
                ? 'Publica desafíos de innovación, recibe postulaciones estructuradas y evalúa con múltiples perspectivas. Sin contratos largos. Sin consultoría obligatoria. Sin fricción.'
                : 'Publish innovation challenges, receive structured applications and evaluate with multiple perspectives. No long contracts. No mandatory consulting. No friction.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button
                asChild
                size="lg"
                className="text-lg px-8 py-6 text-purple-900 font-semibold"
                style={{ backgroundColor: '#ffffff' }}
              >
                <Link to="/pricing">
                  {t.hero.ctaPrimary} <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-purple-900 text-lg px-8 py-6"
              >
                <Link to="/programs">{t.hero.ctaSecondary}</Link>
              </Button>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="px-4 py-2 bg-white/10 rounded-full text-white text-sm">
                {t.hero.tag1}
              </span>
              <span className="px-4 py-2 bg-white/10 rounded-full text-white text-sm">
                {t.hero.tag2}
              </span>
              <span className="px-4 py-2 bg-white/10 rounded-full text-white text-sm">
                {t.hero.tag3}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Desafíos Activos */}
      {featuredPrograms.length > 0 && (
        <section className="py-20" style={{ backgroundColor: '#f8f6ff' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                {language === 'es' ? 'Desafios Activos' : 'Active Challenges'}
              </h2>
              <p className="text-lg text-gray-600">
                {language === 'es' ? 'Explora desafios publicados por organizaciones reales' : 'Explore challenges published by real organizations'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {featuredPrograms.map((program) => {
                const days = Math.ceil((new Date(program.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                return (
                  <Link key={program._id} to={`/programs/${program._id}`} className="group block" data-testid={`home-featured-${program._id}`}>
                    <Card className="border border-gray-200 hover:border-purple-400 transition-all duration-300 hover:shadow-lg overflow-hidden h-full">
                      <div className="h-44 overflow-hidden relative">
                        <img
                          src={getImageUrl(program.image)}
                          alt={program.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {program.featured && (
                          <div className="absolute top-3 left-3 px-2 py-1 text-xs font-bold rounded text-white" style={{ backgroundColor: '#ff9400' }}>
                            {language === 'es' ? 'DESTACADO' : 'FEATURED'}
                          </div>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <p className="text-xs font-medium mb-1 uppercase tracking-wide" style={{ color: '#6949b6' }}>
                          {program.organization}
                        </p>
                        <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-purple-700 transition-colors">
                          {program.name}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-gray-500 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{program.startDate ? new Date(program.startDate).toLocaleDateString(language === 'es' ? 'es-CL' : 'en-US', { day: 'numeric', month: 'short' }) : ''} - {new Date(program.deadline).toLocaleDateString(language === 'es' ? 'es-CL' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            <span>{program.applicants}</span>
                          </div>
                        </div>
                        {program.challenge && (
                          <p className="text-xs text-gray-500 mt-2 line-clamp-2">{(() => { const doc = new DOMParser().parseFromString(program.challenge, 'text/html'); return doc.body.textContent || ''; })()}</p>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
            <div className="text-center">
              <Button
                asChild
                variant="outline"
                className="border-2 px-8"
                style={{ borderColor: '#6949b6', color: '#6949b6' }}
              >
                <Link to="/programs">
                  {language === 'es' ? 'Ver todos los desafios' : 'View all challenges'} <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* 3. Cómo Funciona - Interactive Carousel */}
      <section className="py-20 bg-gray-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              {t.howItWorks.title}
            </h2>
            <p className="text-lg text-purple-600 font-medium">
              {t.howItWorks.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left: Step cards */}
            <div className="lg:col-span-4 space-y-3">
              {howItWorks.map((step, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveStep(idx)}
                  data-testid={`step-card-${idx}`}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-300 ${
                    activeStep === idx
                      ? 'bg-white border-blue-500 shadow-lg'
                      : 'bg-white border-gray-100 hover:border-gray-200 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-colors ${
                        activeStep === idx ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {step.step}
                    </div>
                    <div>
                      <h3 className={`text-base font-semibold mb-1 transition-colors ${activeStep === idx ? 'text-gray-900' : 'text-gray-600'}`}>
                        {step.title}
                      </h3>
                      {activeStep === idx && (
                        <p className="text-sm text-gray-500 leading-relaxed">{step.description}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Right: Screenshot + details */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-lg overflow-hidden">
                {/* Screenshot */}
                <div className="relative bg-gray-900 p-2">
                  {/* Browser dots */}
                  <div className="flex items-center gap-1.5 px-3 py-2">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <div className="flex-1 mx-4">
                      <div className="bg-gray-700 rounded-md px-3 py-1 text-xs text-gray-400 text-center">colaborachile.cl</div>
                    </div>
                  </div>
                  <img
                    src={`/images/steps/step${activeStep + 1}.jpg`}
                    alt={howItWorks[activeStep]?.title}
                    className="w-full rounded-sm"
                    style={{ maxHeight: '420px', objectFit: 'cover', objectPosition: 'top' }}
                    data-testid="step-screenshot"
                  />
                </div>

                {/* Details below image */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-bold uppercase tracking-widest text-blue-500">
                      {language === 'es' ? `Paso ${activeStep + 1} de 4` : `Step ${activeStep + 1} of 4`}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{howItWorks[activeStep]?.title}</h3>
                  <p className="text-gray-600 mb-4">{howItWorks[activeStep]?.description}</p>

                  {/* Carousel dots + Next */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex gap-2">
                      {howItWorks.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveStep(idx)}
                          className={`w-2.5 h-2.5 rounded-full transition-all ${activeStep === idx ? 'bg-blue-500 w-6' : 'bg-gray-300 hover:bg-gray-400'}`}
                          data-testid={`step-dot-${idx}`}
                        />
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-blue-200 text-blue-600 hover:bg-blue-50"
                      onClick={() => setActiveStep(prev => (prev + 1) % 4)}
                      data-testid="next-step-btn"
                    >
                      {language === 'es' ? 'Siguiente paso' : 'Next step'} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-500 mt-10 text-base">
            {t.howItWorks.footer}
          </p>
        </div>
      </section>

      {/* 4. Qué Ofrecemos */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t.features.title}
            </h2>
            <p className="text-lg text-gray-600">
              {t.features.subtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-purple-200 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <div 
                      className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#f3f0ff', color: '#6949b6' }}
                    >
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center p-6 bg-purple-50 rounded-xl">
            <p className="text-purple-800 font-medium">
              {t.features.moreSupport}
            </p>
          </div>
        </div>
      </section>

      {/* AI Evaluators Showcase */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text + CTA */}
            <div>
              <Badge className="mb-4 bg-amber-100 text-amber-800 border-amber-200 text-xs font-semibold px-3 py-1">
                {language === 'es' ? 'Disponible desde plan Starter' : 'Available from Starter plan'}
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {language === 'es'
                  ? 'Evaluadores IA que analizan cada postulacion en profundidad'
                  : 'AI Evaluators that analyze every application in depth'}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {language === 'es'
                  ? 'Nuestros evaluadores IA expertos puntuan y comentan cada criterio de evaluacion, entregando feedback detallado y comparable con evaluadores humanos.'
                  : 'Our expert AI evaluators score and comment on each evaluation criterion, delivering detailed feedback comparable to human evaluators.'}
              </p>
              <ul className="space-y-3 mb-8">
                {(language === 'es' ? [
                  'Multiples perfiles IA con distintas perspectivas',
                  'Puntaje y comentarios por cada criterio',
                  'Comentario general de cada evaluador',
                  'Resultados inmediatos para todas las postulaciones'
                ] : [
                  'Multiple AI profiles with different perspectives',
                  'Score and comments per criterion',
                  'Overall comment from each evaluator',
                  'Immediate results for all applications'
                ]).map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="text-white font-semibold px-6" style={{ backgroundColor: '#6949b6' }}>
                  <Link to="/team">
                    {language === 'es' ? 'Conoce al equipo IA' : 'Meet the AI team'} <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-gray-300 text-gray-700 font-semibold px-6">
                  <Link to="/register?plan=starter">
                    {language === 'es' ? 'Probar evaluadores IA' : 'Try AI evaluators'}
                  </Link>
                </Button>
              </div>
            </div>
            {/* Right: Image */}
            <div className="flex justify-center lg:justify-end">
              <img
                src="https://customer-assets.emergentagent.com/job_78e4354f-c3d1-4776-8bc6-7f8d8b40d622/artifacts/ogopoxcc_evaluador_ia.png"
                alt={language === 'es' ? 'Evaluadores IA en accion' : 'AI Evaluators in action'}
                className="w-full max-w-lg rounded-2xl"
                data-testid="ai-evaluators-showcase-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* For All Types of Organizations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              {language === 'es' ? 'Para todos los tipos de organizaciones' : 'For all types of organizations'}
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              {language === 'es'
                ? 'Desde corporaciones hasta instituciones publicas, nuestra plataforma se adapta a tus necesidades.'
                : 'From corporations to public institutions, our platform adapts to your needs.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1: Corporate Leaders */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mb-5">
                <Briefcase className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {language === 'es' ? 'Lideres Corporativos' : 'Corporate Leaders'}
              </h3>
              <p className="text-gray-600 mb-5 leading-relaxed">
                {language === 'es'
                  ? 'Encuentra soluciones innovadoras a desafios estrategicos conectando con emprendedores, startups y centros de investigacion.'
                  : 'Find innovative solutions to strategic challenges by connecting with entrepreneurs, startups and research centers.'}
              </p>
              <div className="space-y-2">
                {(language === 'es' ? ['Innovacion abierta', 'Busqueda de proveedores tecnologicos', 'Desarrollo de pilotos']
                  : ['Open innovation', 'Tech vendor scouting', 'Pilot development']).map((item, i) => (
                  <p key={i} className="text-sm text-blue-600 font-medium flex items-center gap-2">
                    <ArrowRight className="w-3.5 h-3.5" /> {item}
                  </p>
                ))}
              </div>
            </div>

            {/* Card 2: Accelerators & Incubators */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center mb-5">
                <GraduationCap className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {language === 'es' ? 'Aceleradoras e Incubadoras' : 'Accelerators & Incubators'}
              </h3>
              <p className="text-gray-600 mb-5 leading-relaxed">
                {language === 'es'
                  ? 'Gestiona convocatorias de seleccion con estructura profesional, evaluacion con IA y seguimiento de postulantes.'
                  : 'Manage selection calls with professional structure, AI evaluation and applicant tracking.'}
              </p>
              <div className="space-y-2">
                {(language === 'es' ? ['Convocatorias de programas', 'Evaluacion con multiples criterios', 'Seleccion de cohortes']
                  : ['Program calls', 'Multi-criteria evaluation', 'Cohort selection']).map((item, i) => (
                  <p key={i} className="text-sm text-purple-600 font-medium flex items-center gap-2">
                    <ArrowRight className="w-3.5 h-3.5" /> {item}
                  </p>
                ))}
              </div>
            </div>

            {/* Card 3: Public Institutions */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 rounded-xl bg-teal-100 flex items-center justify-center mb-5">
                <Landmark className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {language === 'es' ? 'Instituciones Publicas' : 'Public Institutions'}
              </h3>
              <p className="text-gray-600 mb-5 leading-relaxed">
                {language === 'es'
                  ? 'Lanza convocatorias de innovacion publica con transparencia, trazabilidad y evaluacion estandarizada.'
                  : 'Launch public innovation calls with transparency, traceability and standardized evaluation.'}
              </p>
              <div className="space-y-2">
                {(language === 'es' ? ['Fondos concursables', 'Desafios de innovacion publica', 'Evaluacion con criterios objetivos']
                  : ['Competitive funds', 'Public innovation challenges', 'Evaluation with objective criteria']).map((item, i) => (
                  <p key={i} className="text-sm text-teal-600 font-medium flex items-center gap-2">
                    <ArrowRight className="w-3.5 h-3.5" /> {item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Modelo */}
      <section className="py-20" style={{ backgroundColor: '#1a1a2e' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Lightbulb className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t.model.title}
          </h2>
          <p className="text-lg text-gray-300 mb-4">
            {t.model.description}
          </p>
          <p className="text-xl text-teal-400 font-medium">
            {t.model.free}
          </p>
        </div>
      </section>

      {/* 6. Métricas */}
      <section className="py-20" style={{ backgroundColor: '#6949b6' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12">
            {t.stats.title}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</p>
                <p className="text-purple-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Organizaciones que confían */}
      <section className="py-16 bg-gray-50" data-testid="trusted-orgs-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-semibold uppercase tracking-widest text-gray-400 mb-10">
            {language === 'es' ? 'Organizaciones que confian en nosotros' : 'Organizations that trust us'}
          </p>
          <div className="flex items-center justify-center gap-12 md:gap-20 flex-wrap">
            {[
              { src: 'https://customer-assets.emergentagent.com/job_d50f053d-b2df-437c-b1da-cfff855e4ba2/artifacts/3xhtsqfm_Logo_Corfo.png', alt: 'Corfo' },
              { src: 'https://customer-assets.emergentagent.com/job_d50f053d-b2df-437c-b1da-cfff855e4ba2/artifacts/si8i998q_logo_ccs-1.png', alt: 'Cámara de Comercio de Santiago' },
              { src: 'https://customer-assets.emergentagent.com/job_d50f053d-b2df-437c-b1da-cfff855e4ba2/artifacts/oiu080cg_Komatsu_company_logos.svg.png', alt: 'Komatsu' },
              { src: 'https://customer-assets.emergentagent.com/job_78e4354f-c3d1-4776-8bc6-7f8d8b40d622/artifacts/b11194dm_logo-labchile-teva-2048x586.png', alt: 'Laboratorio Chile Teva' },
              { src: 'https://customer-assets.emergentagent.com/job_78e4354f-c3d1-4776-8bc6-7f8d8b40d622/artifacts/6dz6f7hi_logo-araucana.png', alt: 'La Araucana' },
              { src: 'https://customer-assets.emergentagent.com/job_78e4354f-c3d1-4776-8bc6-7f8d8b40d622/artifacts/5zpycwdm_colbun_logo.png', alt: 'Colbun' },
            ].map((logo) => (
              <div key={logo.alt} className="h-14 w-40 flex items-center justify-center" data-testid={`trusted-logo-${logo.alt.toLowerCase().replace(/\s+/g, '-')}`}>
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="max-h-full max-w-full object-contain"
                  style={{ mixBlendMode: 'multiply' }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Planes */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t.pricing.title}
            </h2>
            <p className="text-lg text-gray-600 mb-8">{t.pricing.subtitle}</p>
            <div className="flex items-center justify-center gap-4">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
                {t.pricing.toggle.monthly}
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                  isAnnual ? 'bg-purple-600' : 'bg-gray-300'
                }`}
                style={{ backgroundColor: isAnnual ? '#6949b6' : undefined }}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-300 ${
                    isAnnual ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
                {t.pricing.toggle.annual}
              </span>
              {isAnnual && (
                <span className="ml-2 px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  {t.pricing.toggle.save}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* Plan Gratis */}
            <Card className="border-2 border-dashed border-gray-300 hover:border-purple-200 transition-all duration-300 bg-gray-50/50">
              <CardContent className="p-6 md:p-8">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {language === 'es' ? 'Gratis' : 'Free'}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold text-gray-700">USD 0</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {language === 'es' ? 'Prueba la plataforma' : 'Try the platform'}
                  </p>
                </div>
                <div className="space-y-3 mb-6">
                  {(language === 'es' ? [
                    '1 desafio publicado',
                    'Recibe postulaciones',
                    'Ver hasta 20 postulaciones',
                    '1 evaluador IA incluido',
                    'Dashboard basico',
                  ] : [
                    '1 published challenge',
                    'Receive applications',
                    'View up to 20 applications',
                    '1 AI evaluator included',
                    'Basic dashboard',
                  ]).map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 pt-4 mb-6">
                  <p className="text-xs text-gray-500 mb-2 font-medium">{language === 'es' ? 'No incluye:' : 'Not included:'}</p>
                  {(language === 'es' ? [
                    'Desafios ilimitados',
                    'Multiples evaluadores IA',
                    'Exportacion de datos',
                    'Soporte',
                  ] : [
                    'Unlimited challenges',
                    'Multiple AI evaluators',
                    'Data export',
                    'Support',
                  ]).map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <X className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-500">{item}</span>
                    </div>
                  ))}
                </div>
                <Button asChild variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-100">
                  <Link to="/register?plan=free">{language === 'es' ? 'Comenzar gratis' : 'Start for free'}</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Plan Starter */}
            <Card className="border-2 border-gray-200 hover:border-purple-300 transition-all duration-300">
              <CardContent className="p-6 md:p-8">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t.pricing.plans.starter.name}</h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold" style={{ color: '#6949b6' }}>
                      USD {isAnnual ? t.pricing.plans.starter.annualPrice : t.pricing.plans.starter.monthlyPrice}
                    </span>
                    <span className="text-gray-500">/ {isAnnual ? t.pricing.period.year : t.pricing.period.month}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t.pricing.plans.starter.tagline}</p>
                </div>
                <div className="space-y-3 mb-6">
                  {t.pricing.plans.starter.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 pt-4 mb-6">
                  <p className="text-xs text-gray-500 mb-2 font-medium">{language === 'es' ? 'No incluye:' : 'Not included:'}</p>
                  {t.pricing.plans.starter.notIncluded.map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <X className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-500">{item}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="w-full text-white" style={{ backgroundColor: '#6949b6' }}>
                  <Link to={`/register?plan=starter&cycle=${isAnnual ? 'annual' : 'monthly'}`}>{t.pricing.plans.starter.cta}</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Plan Growth */}
            <Card className="border-2 border-purple-400 shadow-xl relative">
              <div 
                className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-white text-sm font-semibold"
                style={{ backgroundColor: '#ff9400' }}
              >
                <Star className="w-4 h-4 inline mr-1" />
                {language === 'es' ? 'Mas Popular' : 'Most Popular'}
              </div>
              <CardContent className="p-6 md:p-8">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t.pricing.plans.growth.name}</h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold" style={{ color: '#6949b6' }}>
                      USD {isAnnual ? t.pricing.plans.growth.annualPrice : t.pricing.plans.growth.monthlyPrice}
                    </span>
                    <span className="text-gray-500">/ {isAnnual ? t.pricing.period.year : t.pricing.period.month}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t.pricing.plans.growth.tagline}</p>
                </div>
                <p className="text-xs font-semibold text-purple-600 mb-3">{t.pricing.plans.growth.includesStarter}</p>
                <div className="space-y-3 mb-6">
                  {t.pricing.plans.growth.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 pt-4 mb-4">
                  <p className="text-xs text-gray-500 mb-2 font-medium">{language === 'es' ? 'No incluye:' : 'Not included:'}</p>
                  {t.pricing.plans.growth.notIncluded.map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <X className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-500">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-600 italic mb-6">{t.pricing.plans.growth.idealFor}</p>
                <Button asChild className="w-full text-white" style={{ backgroundColor: '#ff9400' }}>
                  <Link to={`/register?plan=growth&cycle=${isAnnual ? 'annual' : 'monthly'}`}>{t.pricing.plans.growth.cta}</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Plan Pro */}
            <Card className="border-2 border-gray-200 hover:border-purple-300 transition-all duration-300 bg-gradient-to-b from-white to-purple-50">
              <CardContent className="p-6 md:p-8">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t.pricing.plans.pro.name}</h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold" style={{ color: '#6949b6' }}>
                      USD {isAnnual ? t.pricing.plans.pro.annualPrice : t.pricing.plans.pro.monthlyPrice}
                    </span>
                    <span className="text-gray-500">/ {isAnnual ? t.pricing.period.year : t.pricing.period.month}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t.pricing.plans.pro.tagline}</p>
                </div>
                <p className="text-xs font-semibold text-purple-600 mb-3">{t.pricing.plans.pro.includesGrowth}</p>
                <div className="space-y-3 mb-6">
                  {t.pricing.plans.pro.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-600 italic mb-6">{t.pricing.plans.pro.idealFor}</p>
                <Button asChild variant="outline" className="w-full border-2" style={{ borderColor: '#6949b6', color: '#6949b6' }}>
                  <a href="mailto:contacto@colaborachile.cl">{t.pricing.plans.pro.cta}</a>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Add-ons */}
          <div className="mt-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.pricing.addons.title}</h3>
              <p className="text-gray-600">{t.pricing.addons.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.pricing.addons.items.map((addon, index) => (
                <Card key={index} className="border border-gray-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <h4 className="font-semibold text-gray-900 mb-1">{addon.name}</h4>
                    <p className="text-lg font-bold mb-2" style={{ color: '#6949b6' }}>{addon.price}</p>
                    <p className="text-sm text-gray-600 mb-3">{addon.description}</p>
                    <p className="text-xs text-gray-500 italic border-t border-gray-100 pt-2">
                      {t.pricing.addons.availableNote}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 9. Quick Registration CTA */}
      <section className="py-20" style={{ backgroundColor: '#1a1a2e' }} data-testid="quick-register-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                {language === 'es' ? 'Lanza tu primer desafio hoy' : 'Launch your first challenge today'}
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {language === 'es'
                  ? 'Crea tu cuenta gratuita en segundos. Publica tu primer desafio, recibe postulaciones y explora el poder de la evaluacion con IA. Sin tarjeta de credito, sin compromisos.'
                  : 'Create your free account in seconds. Publish your first challenge, receive applications and explore the power of AI evaluation. No credit card, no commitments.'}
              </p>
              <ul className="space-y-3">
                {(language === 'es' ? [
                  'Plan gratuito con 1 desafio activo',
                  'Hasta 20 postulaciones visibles',
                  '1 evaluador IA incluido',
                  'Acceso a plantillas guiadas'
                ] : [
                  'Free plan with 1 active challenge',
                  'Up to 20 visible applications',
                  '1 AI evaluator included',
                  'Access to guided templates'
                ]).map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-teal-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Form */}
            <div className="bg-white rounded-2xl p-8 shadow-xl" data-testid="quick-register-form">
              {quickRegDone ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {language === 'es' ? 'Cuenta creada!' : 'Account created!'}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {language === 'es' ? 'Ya puedes iniciar sesion y publicar tu primer desafio.' : 'You can now log in and publish your first challenge.'}
                  </p>
                  <Button asChild className="text-white" style={{ backgroundColor: '#6949b6' }}>
                    <Link to="/login">{language === 'es' ? 'Iniciar sesion' : 'Log in'}</Link>
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {language === 'es' ? 'Crea tu cuenta gratis' : 'Create your free account'}
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    {language === 'es' ? 'Comienza a publicar desafios en menos de 2 minutos' : 'Start publishing challenges in less than 2 minutes'}
                  </p>
                  {quickRegError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4" data-testid="quick-reg-error">
                      {quickRegError}
                    </div>
                  )}
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    setQuickRegError('');
                    if (!quickReg.name || !quickReg.email || !quickReg.organization) {
                      setQuickRegError(language === 'es' ? 'Todos los campos son requeridos' : 'All fields are required');
                      return;
                    }
                    setQuickRegLoading(true);
                    try {
                      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
                        email: quickReg.email,
                        password: quickReg.email.split('@')[0] + '2026!',
                        name: quickReg.name,
                        organization: quickReg.organization,
                        role: 'organization',
                      });
                      setQuickRegDone(true);
                    } catch (err) {
                      const detail = err.response?.data?.detail || '';
                      setQuickRegError(detail || (language === 'es' ? 'Error al crear la cuenta' : 'Error creating account'));
                    } finally {
                      setQuickRegLoading(false);
                    }
                  }} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'es' ? 'Nombre completo' : 'Full name'}
                      </label>
                      <Input
                        value={quickReg.name}
                        onChange={e => setQuickReg(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={language === 'es' ? 'Ej: Maria Lopez' : 'E.g. Maria Lopez'}
                        data-testid="quick-reg-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'es' ? 'Email corporativo' : 'Corporate email'}
                      </label>
                      <Input
                        type="email"
                        value={quickReg.email}
                        onChange={e => setQuickReg(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="nombre@empresa.cl"
                        data-testid="quick-reg-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'es' ? 'Nombre de la organizacion' : 'Organization name'}
                      </label>
                      <Input
                        value={quickReg.organization}
                        onChange={e => setQuickReg(prev => ({ ...prev, organization: e.target.value }))}
                        placeholder={language === 'es' ? 'Ej: Empresa S.A.' : 'E.g. Company Inc.'}
                        data-testid="quick-reg-org"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={quickRegLoading}
                      className="w-full text-white text-base py-5"
                      style={{ backgroundColor: '#6949b6' }}
                      data-testid="quick-reg-submit"
                    >
                      {quickRegLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {language === 'es' ? 'Creando...' : 'Creating...'}</>
                      ) : (
                        <>{language === 'es' ? 'Crear cuenta gratis' : 'Create free account'} <ArrowRight className="ml-2 w-4 h-4" /></>
                      )}
                    </Button>
                    <p className="text-xs text-gray-400 text-center">
                      {language === 'es'
                        ? 'Al registrarte aceptas nuestros terminos de servicio. Se generara una contraseña temporal que podras cambiar desde tu perfil.'
                        : 'By registering you accept our terms of service. A temporary password will be generated that you can change from your profile.'}
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
